import { useEffect, useState } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import DeleteSvg from "../../assets/svg/deleteSvg";
import { useAuthUser } from "../../customHooks/useAuthUser";
import Login from "../Login";
import { useSelector } from "react-redux";
import ExportApi from "../../api/ExportApi";
import { toast } from "react-toastify";

const PriceAlertModalContent = ({
  setShowPriceAlert = () => { },
  product = {}, 
  getAlerts = () => {},
  fromDashboard = false
}) => {
  const { model, series } = useSelector(
    (state) => state.filters
  );

  const loggedInUser = useAuthUser();
  const [modal, setModal] = useState({});
  const [signup, setSignup] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [showAuthModule, setShowAuthModule] = useState(false);
  const [alerts, setAlerts] = useState([
    { series: "", model: "ALL", price: "" }
  ]);
  const [errors, setErrors] = useState([]);
  const [tenAlertsComp, setTenAlertsComp] = useState(false);

  useEffect(() => {
    if (product && product.series && product.model) {
      setAlerts([
        {
          series: product.series || "",
          model: product.model || "ALL",
          price: ""
        }
      ]);
    }
  }, [product]);

  const addAlertRow = () => {
    if (alerts?.length >= 10) {
      setTenAlertsComp(true);
      toast.error("You can add up to 10 price alerts only.");
      return;
    }
    setAlerts([...alerts, { series: "", model: "ALL", price: "" }]);
  };

  const deleteRow = (index) => {
    if (alerts?.length <= 10) {
      setTenAlertsComp(false);
    }

    const updated = alerts.filter((_, i) => i !== index);
    setAlerts(updated);
  };

  const handleChange = (index, field, value) => {
 const updated = [...alerts];
    updated[index][field] = value;

    if (field === "series") updated[index].model = "ALL";
    setAlerts(updated);

  // clear error for this specific field
  const updatedErrors = [...errors];
  if (updatedErrors[index]) {
    updatedErrors[index] = {
      ...updatedErrors[index],
      [field]: ""
    };
  }
  setErrors(updatedErrors);
};

  const handleAlertSave = () => {
    if (!loggedInUser) {
      setShowAuthModule(true);
      return;
    }

    let isValid = true;
    const newErrors = alerts.map((row) => {
      const rowErrors = {
        series: "",
        model: "",
        price: ""
      };

      if (!row.series || row.series?.trim() === "") {
        rowErrors.series = "Series is required";
        isValid = false;
      }

      if (!row.model || row.model?.trim() === "") {
        rowErrors.model = "Model is required";
        isValid = false;
      }

      if (!row.price || Number(row.price) <= 0) {
        rowErrors.price = "Price is required";
        isValid = false;
      }

      return rowErrors;
    });

    setErrors(newErrors);

    if (!isValid) return;

    // All validations passed — call your API here
    ExportApi.createPriceAlert(alerts)
      .then((response) => {
        if (response.data.success) {
          toast.success("Alerts created successfully.");
          setShowPriceAlert(false);
          if (fromDashboard) {
            getAlerts()
          }
        } else {
          toast.error(response.data.message || "Failed to create the alert, Please try again.");
          setShowPriceAlert(false);
        }
      })
      .catch((error) => {
        toast.error("Failed to create the alert, Please try again.");
        setShowPriceAlert(false);
        console.error("Error fetching negative series:", error);
      });
  };

  return (
    <div style={{ backgroundColor: "#eee" }} className="py-3">
      {/* Heading */}
      <h4 className="pb-3" style={{ fontWeight: 700 }}>Price Alerts</h4>


      <div className="px-4 pb-4" style={{ borderRadius: "10px" }}>

        {/* Subheading */}
        <p
          style={{
            fontSize: "clamp(0.9rem, 1.2vw, 1.125rem)",
            color: "#212529",
            fontWeight: 500
          }}
          className="">
          We'll email you when a Buy Now Price is at or below the specified amount.
        </p>

        {/* Scrollable Alerts Section */}
        <div
          style={{
            maxHeight: "40vh",
            overflowY: "auto",
            overflowX: "hidden",
            paddingRight: 5
          }}
        >
          {alerts.map((row, index) => (
            <Row className="align-items-center mb-3 g-3" key={index}>

              {/* Series */}
              <Col md={4}>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <Form.Select
                    value={row.series}
                    onChange={(e) => handleChange(index, "series", e.target.value)}
                  >
                    <option value="">Select Series</option>
                    {series.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </Form.Select>

                  {/* Reserve space: height: 18px keeps layout stable */}
                  <div style={{ height: "18px" }}>
                    {errors[index]?.series && (
                      <small className="text-danger">{errors[index].series}</small>
                    )}
                  </div>
                </div>
              </Col>

              {/* Model */}
              <Col md={4}>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <Form.Select
                    value={row.model}
                    disabled={!row.series}
                    onChange={(e) => handleChange(index, "model", e.target.value)}
                  >
                    <option value="ALL">ALL</option>
                    {model.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </Form.Select>

                  {/* Reserved space for error */}
                  <div style={{ height: "18px" }}>
                    {errors[index]?.model && (
                      <small className="text-danger">{errors[index].model}</small>
                    )}
                  </div>
                </div>
              </Col>

              {/* Price */}
              <Col md={3}>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <Form.Control
                    type="number"
                    placeholder="Target Price"
                    min="1"
                    value={row.price}
                    onChange={(e) => handleChange(index, "price", e.target.value)}
                  />

                  {/* Reserved space for error */}
                  <div style={{ height: "18px" }}>
                    {errors[index]?.price && (
                      <small className="text-danger">{errors[index].price}</small>
                    )}
                  </div>
                </div>
              </Col>

              {/* Delete */}
              <Col
                md={1}
                className="d-flex justify-content-center align-items-center mt-0 pt-0"
              >
                <span
                  onClick={() => deleteRow(index)}
                >
                  <DeleteSvg />
                </span>
              </Col>

            </Row>
          ))}
        </div>

        {/* Buttons Section */}
        <div className="mt-2">

          {/* Add Alert */}
          <Button
            variant="dark"
            onClick={() => {
              setErrors([]);
              addAlertRow();
            }}
            className={`w-100 mb-3 ${tenAlertsComp ? "opacity-50 pointer-events-none" : ""
              }`}
           
          >
            + Add Alert
          </Button>

          {/* Save + Close */}
          <div className="d-flex gap-3">
            <Button onClick={handleAlertSave} className="w-50" variant="dark">
              Save
            </Button>

            <Button onClick={() => setShowPriceAlert(false)} className="w-50" variant="outline-dark">
              Close
            </Button>
          </div>
        </div>
      </div>

      {/* Login/Signup Section */}
      {
        showAuthModule && !loggedInUser && (
          <div className="mx-4 p-3" style={{ borderRadius: "10px", backgroundColor: "#fff" }}>

            <h4 style={{ fontWeight: 700 }}>{signup ? "Sign Up" : forgot ? "Forgot Password" : "Login"}</h4>

            <Login
              useModal={false}
              setSignup={setSignup}
              setForgot={setForgot}
              signup={signup}
              forgot={forgot}
              modal={() => setModal(false)}
              customButtonStyle={true}
              priceAlertModal={true} />
          </div>
        )
      }

      <style>
        {
          `
    .modal-body {
    padding : 0px !important;
    margin : 0px !important
    }
    `
        }
      </style>
    </div>
  );
};

export default PriceAlertModalContent;
